import { useEffect } from 'react'
import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { useIncomeDash } from '../hooks/DashIncome'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../ui/card'
import { Button } from '../../ui/button'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../ui/table'
import { Line, LineChart, ResponsiveContainer, XAxis, YAxis, Tooltip, Legend } from 'recharts'
import { DollarSign, TrendingUp, Calendar, Wallet, Loader2, TrendingDown } from 'lucide-react'
import AnimatedBackground from '../../utils/AnimatedBackground'
import { IncomeData, TrendData } from '../models/ModelIncome';
import TopBar from '../../utils/TopBar';
import Footer from '../../utils/Footer';

function IncomeDataTable({ data }: { data: IncomeData[] }) {
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead className="text-blue-400">Date</TableHead>
          <TableHead className="text-blue-400">Gross Income</TableHead>
          <TableHead className="text-blue-400">Net Income</TableHead>
          <TableHead className="text-blue-400">Investments</TableHead>
          <TableHead className="text-blue-400">Other Net Income</TableHead>
          <TableHead className="text-blue-400">Monthly Income</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {data.map((income, index) => (
          <TableRow key={index}>
            <TableCell className="text-gray-300">
              {new Date(income.date).toLocaleDateString(undefined, { month: 'long', year: 'numeric' })}
            </TableCell>
            <TableCell className="text-gray-300">{income.gross_income.toLocaleString()}</TableCell>
            <TableCell className="text-gray-300">{income.net_income.toLocaleString()}</TableCell>
            <TableCell className="text-gray-300">{income.investments_income.toLocaleString()}</TableCell>
            <TableCell className="text-gray-300">{income.other_net_income.toLocaleString()}</TableCell>
            <TableCell className="text-gray-300">{income.monthly_income.toLocaleString()}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  )
}

function IncomeTrendChart({ data }: { data: TrendData }) {
  if (!data?.labels) return null

  const chartData = data.labels.map((label, index) => ({
    name: label,
    ...data.datasets.reduce((acc, dataset) => ({
      ...acc,
      [dataset.name]: dataset.values[index]
    }), {})
  }))

  return (
    <ResponsiveContainer width="100%" height={400}>
      <LineChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 20 }}>
        <XAxis 
          dataKey="name" 
          stroke="#888888"
          tickFormatter={(value) => {
            const [year, month] = value.split('-');
            return new Date(parseInt(year), parseInt(month) - 1).toLocaleDateString('default', { 
              month: 'short',
              year: '2-digit'
            });
          }}
        />
        <YAxis 
          stroke="#888888"
          tickFormatter={(value) => `R$ ${value.toLocaleString()}`}
        />
        <Tooltip 
          contentStyle={{ backgroundColor: '#1f2937', border: 'none', borderRadius: '8px' }}
          labelStyle={{ color: '#9ca3af' }}
          formatter={(value: number) => [`R$ ${value.toLocaleString()}`, '']}
          labelFormatter={(label) => {
            const [year, month] = label.split('-');
            return new Date(parseInt(year), parseInt(month) - 1).toLocaleDateString('default', { 
              month: 'long',
              year: 'numeric'
            });
          }}
        />
        <Legend
          verticalAlign="bottom"
          height={36}
          iconType="circle"
          iconSize={8}
          wrapperStyle={{
            paddingBottom: '20px',
            color: '#888888'
          }}
        />
        {data.datasets.map((dataset) => (
          <Line
            key={dataset.name}
            type="monotone"
            dataKey={dataset.name}
            stroke={dataset.color}
            strokeWidth={2}
            dot={true}
            name={dataset.name}
          />
        ))}
      </LineChart>
    </ResponsiveContainer>
  )
}

export default function IncomeDashboard() {
  const { incomeData, trendData, overview, fetchIncomeData, isRecalculating, isLoading } = useIncomeDash()

  useEffect(() => {
    fetchIncomeData()
  }, [fetchIncomeData])

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
        <div className="flex items-center gap-2 text-blue-400">
          <Loader2 className="h-8 w-8 animate-spin" />
          <span className="text-lg">Loading income data...</span>
        </div>
      </div>
    );
  }

  if (!incomeData || incomeData.length === 0) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
        <div className="text-center text-gray-400">
          <h2 className="text-xl font-semibold mb-2">No income data available</h2>
          <p>Please add some income data to see your dashboard.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0a] flex flex-col relative">
      <AnimatedBackground />
      <div className="relative z-10 flex flex-col min-h-screen">
        <TopBar 
          title="Income Dashboard"
          subtitle="Track and analyze your income data"
        />
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 flex justify-between items-center">
          {isRecalculating && (
            <div className="flex items-center gap-2 text-blue-400">
              <Loader2 className="h-4 w-4 animate-spin" />
              <span>Updating data...</span>
            </div>
          )}
        </div>
        <main className="flex-grow container mx-auto py-2 px-4 sm:px-6 lg:px-8">
          <div className="grid gap-6 md:grid-cols-3 lg:grid-cols-4">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="h-full"
            >
              <Card className="bg-black border-gray-800 h-full">
                <CardHeader>
                  <CardTitle className="text-white">Income Overview</CardTitle>
                  <CardDescription className="text-gray-300">Last 12 months of data</CardDescription>
                </CardHeader>
                <CardContent className="flex flex-col gap-5">
                  <div className="flex items-center gap-3">
                    <div className="bg-blue-100 p-2.5 rounded-full">
                      <DollarSign className="w-4.5 h-4.5 text-blue-600" />
                    </div>
                    <div className="flex-grow">
                      <p className="text-gray-400 text-sm">Total Income</p>
                      <div className="flex items-center gap-2">
                        <p className="text-xl font-bold text-white">
                          R$ {overview?.total_income.toLocaleString()}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <div className="bg-yellow-100 p-2.5 rounded-full">
                      <TrendingUp className="w-4.5 h-4.5 text-yellow-600" />
                    </div>
                    <div className="flex-grow">
                      <p className="text-gray-400 text-sm">Investment Gains</p>
                      <div className="flex items-center gap-2">
                        <p className="text-xl font-bold text-white">
                          R$ {overview?.total_investments_income.toLocaleString() ?? 0}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <div className="bg-green-100 p-2.5 rounded-full">
                      {(overview?.monthly_growth ?? 0) < 0 ? (
                        <TrendingDown className="w-4.5 h-4.5 text-red-600" />
                      ) : (
                        <TrendingUp className="w-4.5 h-4.5 text-green-600" />
                      )}
                    </div>
                    <div className="flex-grow">
                      <p className="text-gray-400 text-sm">Monthly Growth</p>
                      <div className="flex items-center gap-2">
                        <p className={`text-xl font-bold ${(overview?.monthly_growth ?? 0) < 0 ? 'text-red-500' : 'text-green-500'}`}>
                          {overview?.monthly_growth !== undefined ? (overview.monthly_growth > 0 ? '+' : '') + overview.monthly_growth : 0}%
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <div className="bg-purple-100 p-2.5 rounded-full">
                      <Calendar className="w-4.5 h-4.5 text-purple-600" />
                    </div>
                    <div className="flex-grow">
                      <p className="text-gray-400 text-sm">Average Monthly</p>
                      <div className="flex items-center gap-2">
                        <p className="text-xl font-bold text-white">
                          R$ {overview?.average_monthly.toLocaleString()}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <div className="bg-orange-100 p-2.5 rounded-full">
                      <Wallet className="w-4.5 h-4.5 text-orange-600" />
                    </div>
                    <div className="flex-grow">
                      <p className="text-gray-400 text-sm">Yearly Projection</p>
                      <div className="flex items-center gap-2">
                        <p className="text-xl font-bold text-white">
                          R$ {overview?.yearly_projection.toLocaleString()}
                        </p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="md:col-span-2 lg:col-span-3 h-full"
            >
              <Card className="bg-black border-gray-800 h-full">
                <CardHeader className="flex flex-row items-center justify-between">
                  <div>
                    <CardTitle className="text-white">Income History</CardTitle>
                    <CardDescription className="text-gray-300">Last 6 months of income data</CardDescription>
                  </div>
                  <div className="flex gap-4">
                    <Link to="/income/edit">
                      <Button className="bg-blue-600 hover:bg-blue-500 text-white">Edit Income</Button>
                    </Link>
                    <Link to="/income/clear">
                      <Button variant="destructive" className="bg-red-600 hover:bg-red-500">Clear Data</Button>
                    </Link>
                  </div>
                </CardHeader>
                <CardContent>
                  <IncomeDataTable data={incomeData} />
                </CardContent>
              </Card>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="md:col-span-3 lg:col-span-4"
            >
              <Card className="bg-black border-gray-800">
                <CardHeader>
                  <CardTitle className="text-white">Income Trend</CardTitle>
                  <CardDescription className="text-gray-300">Monthly income trends over time</CardDescription>
                </CardHeader>
                <CardContent>
                  {trendData && <IncomeTrendChart data={trendData} />}
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </main>
        <Footer />
      </div>
    </div>
  )
}

export { IncomeDashboard }

